/**
 * New Strategy / edit dialog (mandate §5) — behind the Strategies screen's live buttons.
 *
 * Saves through `prepare.strategies.create` / `.update` and the grid re-renders from a fresh
 * `prepare.strategies.list` read-back. Stats are the server's roll-up of tagged trades — never typed here.
 */
import type { ViewModel } from '@/screens/generated/view-model';
import { css } from '@pointfive/ui';

const overlay = css(
  'position:fixed;inset:0;z-index:70;display:flex;align-items:center;justify-content:center;background:color-mix(in srgb, var(--bg) 55%, transparent);backdrop-filter:blur(2px)',
);
const sheet = css(
  'width:380px;max-width:calc(100vw - 40px);border-radius:16px;border:1px solid var(--card-line);background:var(--surface);box-shadow:var(--shadow-float);padding:18px',
);
const field = css(
  'width:100%;padding:9px 12px;border-radius:10px;border:1px solid var(--l10);background:var(--l2);color:var(--text);font-size:12.5px',
);
const label = css(
  'font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:var(--muted2);font-weight:600;margin:10px 0 4px',
);
const ghostBtn = css(
  'font-size:11.5px;padding:7px 14px;border-radius:99px;border:1px solid var(--l10);background:transparent;color:var(--text-mid);cursor:pointer',
);
const primaryBtn = css(
  'font-size:11.5px;font-weight:600;padding:7px 15px;border-radius:99px;border:1px solid color-mix(in srgb, var(--slate-fill) 50%, transparent);background:color-mix(in srgb, var(--slate-fill) 10%, transparent);color:var(--slate);cursor:pointer',
);

export function StrategyDialog({ m }: { m: Partial<ViewModel> }) {
  const e = m.stratEditorOpen;
  if (!e) return null;
  const creating = e.id === null;
  return (
    <div
      style={overlay}
      role="dialog"
      aria-modal="true"
      aria-label={creating ? 'New strategy' : `Edit ${e.name}`}
    >
      <div style={sheet}>
        <h2 style={css('font-size:13px;font-weight:600;margin:0 0 6px')}>
          {creating ? 'New strategy' : 'Edit strategy'}
        </h2>
        <p
          id="pf-strategy-editor-note"
          style={css('font-size:11.5px;color:var(--text-mid);line-height:1.6;margin:0 0 6px')}
        >
          Stats roll up from the trades you tag with it — the card fills in as the ledger does.
        </p>
        <div style={label}>Name</div>
        <input
          style={field}
          aria-label="Strategy name"
          aria-describedby="pf-strategy-editor-note"
          maxLength={64}
          value={e.name}
          onChange={(ev) => m.stratEditorSet?.('name', ev.target.value)}
        />
        <div style={label}>Sessions (comma-separated)</div>
        <input
          style={field}
          aria-label="Sessions, comma-separated"
          placeholder="London, New York"
          value={e.sessions}
          onChange={(ev) => m.stratEditorSet?.('sessions', ev.target.value)}
        />
        <div style={label}>Rules (comma-separated)</div>
        <input
          style={field}
          aria-label="Rules, comma-separated"
          placeholder="Max 2 per session, Only in declared window"
          value={e.rules}
          onChange={(ev) => m.stratEditorSet?.('rules', ev.target.value)}
        />
        <div style={css('display:flex;gap:8px;justify-content:flex-end;margin-top:14px')}>
          <button style={ghostBtn} onClick={m.stratEditorCancel}>
            Cancel
          </button>
          <button
            style={primaryBtn}
            onClick={m.stratEditorSave}
            aria-label={creating ? 'Create the strategy' : 'Save the strategy'}
          >
            {creating ? 'Create strategy' : 'Save strategy'}
          </button>
        </div>
      </div>
    </div>
  );
}
